import { EmptystateChartColored, InfoCircle } from "components/icons/generated";
import Box from "ds/components/Box";
import Card from "ds/components/Card";
import EmptyState from "ds/components/EmptyState";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";

import UsageWorkersNewHeader from "../Header";
import styles from "./styles.module.css";
import UsageWorkersNewMetricCards from "../MetricCards";
import ConcurrencyChart from "../ConcurrencyChart";
import { FRESHNESS_LABEL } from "../constants";
import {
  formatWorkerUsageDate,
  getWorkerUsageCycleDay,
  getWorkerUsageCycleTotalDays,
  getWorkerUsagePeak,
  hasWorkerUsageContract,
  isWorkerUsageAboveContract,
  isWorkerUsageEmpty,
} from "../utils";
import { WorkerUsageData } from "../types";

const CHART_DISCLAIMER =
  "These values are only directional. The percentile is computed over the cycle so far and is finalized on a monthly basis. The data is covering solely the current account and isn't covering data from other accounts of the same organization.";

const TREND_DISCLAIMER =
  "We can't display your contracted limit right now, so this page is showing usage trend only.";

type UsageWorkersNewContentProps = {
  usage: WorkerUsageData;
};

const buildSummary = (usage: WorkerUsageData) => {
  if (!hasWorkerUsageContract(usage)) {
    const percentileText =
      usage.fallbackPercentileValue !== undefined
        ? ` P${usage.fallbackPercentile} to date ${usage.fallbackPercentileValue} workers.`
        : "";

    return `Worker usage trend. Usage is not measured against a contract.${percentileText} Peak ${getWorkerUsagePeak(
      usage
    )} workers. ${FRESHNESS_LABEL}`;
  }

  const cycleDay = getWorkerUsageCycleDay(usage);
  const cycleTotalDays = getWorkerUsageCycleTotalDays(usage);
  const cycleRange = `Billing cycle ${formatWorkerUsageDate(
    usage.cycleStart,
    "d MMM"
  )} to ${formatWorkerUsageDate(usage.cycleEnd, "d MMM yyyy")}.`;
  const opening = `Worker usage, current billing cycle, day ${cycleDay} of ${cycleTotalDays}.`;

  const { contractedWorkers, contractedPercentile, percentileToDateValue } = usage;
  const verdictText = isWorkerUsageAboveContract(usage) ? "above contract" : "within contract";

  return `${opening} Contracted ${contractedWorkers} workers at P${contractedPercentile}. P${contractedPercentile} to-date ${percentileToDateValue} workers, ${verdictText}. ${cycleRange} ${FRESHNESS_LABEL}`;
};

const UsageWorkersNewContent = ({ usage }: UsageWorkersNewContentProps) => {
  const hasContract = hasWorkerUsageContract(usage);

  if (isWorkerUsageEmpty(usage)) {
    return (
      <>
        <UsageWorkersNewHeader usage={usage} />

        <Box align="center" justify="center" grow="1">
          <EmptyState
            title="No private worker usage"
            icon={EmptystateChartColored}
            caption="This page counts private workers only. Runs on Spacelift's public workers are not included."
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <UsageWorkersNewHeader usage={usage} />

      <Box direction="column" padding="x-large x-large xx-large" gap="large" grow="1">
        <UsageWorkersNewMetricCards usage={usage} />

        <Card
          title={hasContract ? "Worker usage this cycle" : "Worker usage"}
          grow="1"
          contentClassName={styles.cardContent}
        >
          <ConcurrencyChart usage={usage} summary={buildSummary(usage)} />

          <Box direction="row" align="start" gap="small" padding="medium xx-large 0 0">
            <Box shrink="0">
              <Icon src={InfoCircle} color="tertiary" />
            </Box>
            <Typography variant="p-body4" tag="p" color="secondary">
              {hasContract ? CHART_DISCLAIMER : TREND_DISCLAIMER}
            </Typography>
          </Box>

          {/* sr-only must sit on a wrapper: table height acts as a minimum, so the
              1px clip never applies to the table itself and it stretches the page. */}
          <div className="sr-only">
            <table>
              <caption>Daily worker usage for the billing cycle</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Maximum workers</th>
                  {hasContract && <th scope="col">P{usage.contractedPercentile} to date</th>}
                  {hasContract && <th scope="col">Burst minutes used</th>}
                </tr>
              </thead>
              <tbody>
                {usage.daily.map((point) => (
                  <tr key={point.day.getTime()}>
                    <th scope="row">{formatWorkerUsageDate(point.day, "EEE d MMM yyyy")}</th>
                    <td>{point.max}</td>
                    {hasContract && <td>{point.percentileToDateValue ?? "—"}</td>}
                    {hasContract && <td>{point.burstMinutesUsed ?? "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Box>
    </>
  );
};

export default UsageWorkersNewContent;
